import React, { useEffect, useRef, useState } from 'react';
import { sendWelcomeEmail } from '../../services/demoApi';

/**
 * Step5_Success — The Grand Finale
 *
 * Three-phase experience:
 *   1. VERIFYING   — "Processing your eSign…" spinner (2s)
 *   2. VERIFIED    — "Verification Complete!" checkmark pop-in (2s)
 *   3. WELCOME     — NeoBank app dashboard interface + email status
 */

const PHASE_DURATIONS = {
  verifying: 2000,
  verified: 2500,
};

const Step5Success = ({ formData, demoState, onRestart }) => {
  const [phase, setPhase] = useState('verifying'); // 'verifying' | 'verified' | 'welcome'
  const [emailStatus, setEmailStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const emailSent = useRef(false);
  const phaseTimer = useRef(null);

  // ─── Extract display name from dynamic formData ─────────────
  const displayName = (() => {
    const fd = formData || {};
    const nameKeys = ['borrower_name', 'first_party_name', 'signer_name', 'name', 'full_name', 'applicant_name'];
    for (const key of nameKeys) {
      if (fd[key]) return fd[key];
    }
    // Fallback: try any value that looks like a name
    for (const [, val] of Object.entries(fd)) {
      if (typeof val === 'string' && val.includes(' ') && val.length > 4 && val.length < 50) {
        return val;
      }
    }
    return 'User';
  })();

  const firstName = displayName.split(' ')[0];

  // ─── Phase transitions ──────────────────────────────────────
  useEffect(() => {
    if (phase === 'verifying') {
      phaseTimer.current = setTimeout(() => setPhase('verified'), PHASE_DURATIONS.verifying);
    } else if (phase === 'verified') {
      phaseTimer.current = setTimeout(() => setPhase('welcome'), PHASE_DURATIONS.verified);
    }
    return () => clearTimeout(phaseTimer.current);
  }, [phase]);

  // ─── Send welcome email when entering "welcome" phase ───────
  useEffect(() => {
    if (phase !== 'welcome') return;
    if (emailSent.current) return;
    if (!demoState?.signerEmail) return;

    emailSent.current = true;
    setEmailStatus('sending');

    sendWelcomeEmail(
      demoState.signerEmail,
      displayName,
      demoState.documentId,
      'NeoBank'
    )
      .then(() => setEmailStatus('sent'))
      .catch(err => {
        console.error('Failed to send welcome email:', err);
        setEmailStatus('error');
      });
  }, [phase, demoState?.signerEmail, demoState?.documentId, displayName]);

  // ─── Phase 1: Verifying ─────────────────────────────────────
  if (phase === 'verifying') {
    return (
      <div className="loan-card" style={{ textAlign: 'center', minHeight: 320 }}>
        <div className="loan-processing" style={{ paddingTop: 48 }}>
          <div className="loan-spinner" style={{ width: 56, height: 56, borderWidth: 4 }} />
          <div className="loan-processing__text" style={{ fontSize: 18, fontWeight: 600, marginTop: 8 }}>
            Processing Your eSign...
          </div>
          <div className="loan-processing__subtext" style={{ marginTop: 0 }}>
            Verifying digital signature and finalizing documents
          </div>
        </div>

        {/* Progress steps */}
        <div style={{ marginTop: 24, textAlign: 'left', maxWidth: 280, margin: '24px auto 0' }}>
          {[
            { label: 'Aadhaar Identity Verified', done: true },
            { label: 'Digital Signature Applied', done: true },
            { label: 'Document Finalized', done: false },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0', fontSize: 13,
              color: item.done ? 'var(--loan-success)' : 'var(--loan-text-tertiary)',
            }}>
              <span style={{ fontSize: 14 }}>{item.done ? '✓' : '○'}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Phase 2: Verified ──────────────────────────────────────
  if (phase === 'verified') {
    return (
      <div className="loan-card" style={{ textAlign: 'center', minHeight: 320 }}>
        <div className="loan-success-circle" style={{ width: 112, height: 112, marginBottom: 20 }}>
          <span className="loan-success-check" style={{ fontSize: 56 }}>✓</span>
        </div>

        <h1 className="loan-heading" style={{
          fontSize: 28, color: 'var(--loan-success)',
          animation: 'loanFadeSlideIn 0.5s ease forwards',
        }}>
          Verification Complete!
        </h1>
        <p className="loan-subheading" style={{ marginBottom: 8 }}>
          All checks passed. Your account is ready.
        </p>
        <p style={{
          fontSize: 12, color: 'var(--loan-text-tertiary)',
          animation: 'loanFadeSlideIn 0.8s ease forwards',
        }}>
          Preparing your dashboard...
        </p>
      </div>
    );
  }

  // ─── Phase 3: Welcome Dashboard ─────────────────────────────
  const currentDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  return (
    <>
      {/* Welcome Header Card */}
      <div className="loan-card" style={{ marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
        {/* Accent bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: 'var(--loan-success)',
        }} />

        <div style={{ textAlign: 'center', paddingTop: 12 }}>
          {/* App avatar */}
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'var(--loan-accent)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', boxShadow: '0 4px 16px rgba(99, 102, 241, 0.25)',
          }}>
            <span style={{ color: '#fff', fontSize: 26, fontWeight: 800, fontFamily: 'var(--loan-font-display)' }}>NB</span>
          </div>

          <div className="loan-eyebrow" style={{ color: 'var(--loan-success)', marginBottom: 4 }}>
            Welcome to NeoBank
          </div>
          <h1 className="loan-heading" style={{ fontSize: 24, marginBottom: 4 }}>
            Hello, {firstName}! 👋
          </h1>
          <p className="loan-subheading" style={{ marginBottom: 20, fontSize: 14 }}>
            Your account is active and your loan agreement is finalized.
          </p>
        </div>

        {/* Account Summary */}
        <div style={{
          background: 'var(--loan-bg-card-alt)', borderRadius: 'var(--loan-radius-md)',
          padding: 20, marginBottom: 4,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--loan-text-secondary)' }}>
              Account Overview
            </span>
            <span className="loan-badge loan-badge--success">Active</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--loan-text-tertiary)', marginBottom: 4 }}>Account Holder</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--loan-text-primary)' }}>{displayName}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--loan-text-tertiary)', marginBottom: 4 }}>Onboarded</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--loan-text-primary)' }}>{currentDate}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--loan-text-tertiary)', marginBottom: 4 }}>Agreement</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--loan-success)' }}>✓ Signed</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--loan-text-tertiary)', marginBottom: 4 }}>Status</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--loan-success)' }}>✓ Verified</div>
            </div>
          </div>
        </div>
      </div>

      {/* Journey Journey Completed Card */}
      <div className="loan-card" style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--loan-text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Onboarding Journey
        </p>

        {[
          { icon: '📝', color: '#a855f7', bg: 'rgba(147,51,234,0.1)', title: 'Document Generated', sub: `DoqSolv — ${demoState?.templateName || 'Template Engine'}` },
          { icon: '📜', color: '#f97316', bg: 'rgba(234,88,12,0.1)', title: 'e-Stamp Attached', sub: 'DocuStamp — Bulk Order Fulfillment' },
          { icon: '🔏', color: '#06b6d4', bg: 'rgba(6,182,212,0.1)', title: 'Digitally Signed', sub: `Aadhaar eSign${demoState?.envelopeId ? ` — ${demoState.envelopeId.slice(0, 10)}…` : ''}` },
          {
            icon: '📧',
            color: '#3b82f6',
            bg: 'rgba(59,130,246,0.1)',
            title: 'Welcome Email',
            sub: emailStatus === 'sending' ? 'Sending welcome email...'
              : emailStatus === 'sent' ? `Sent to ${demoState?.signerEmail || 'signer'}`
              : emailStatus === 'error' ? 'Email delivery failed'
              : demoState?.signerEmail ? 'Preparing...' : 'No email on file',
          },
        ].map((item, i) => (
          <div className="loan-info-row" key={i}>
            <div className="loan-info-row__icon" style={{ background: item.bg, color: item.color }}>{item.icon}</div>
            <div className="loan-info-row__content">
              <div className="loan-info-row__title">{item.title}</div>
              <div className="loan-info-row__subtitle">{item.sub}</div>
            </div>
            <div className={`loan-badge ${
              i === 3
                ? (emailStatus === 'sent' ? 'loan-badge--success' : emailStatus === 'error' ? 'loan-badge--error' : 'loan-badge--accent')
                : 'loan-badge--success'
            }`}>
              {i === 3
                ? (emailStatus === 'sending' ? '...' : emailStatus === 'sent' ? 'Sent' : emailStatus === 'error' ? 'Failed' : '...')
                : 'Done'
              }
            </div>
          </div>
        ))}
      </div>

      {/* API Reference (collapsible detail) */}
      {demoState?.documentId && (
        <div className="loan-card" style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--loan-text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            API References
          </p>
          <div style={{ fontSize: 12, color: 'var(--loan-text-tertiary)', lineHeight: 2.2 }}>
            {demoState.documentId && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>DoqSolv Document</span>
                <code style={{ fontSize: 10, background: 'var(--loan-bg-card-alt)', padding: '2px 6px', borderRadius: 4 }}>
                  {demoState.documentId.slice(0, 16)}…
                </code>
              </div>
            )}
            {demoState.stampDocumentId && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Stamp Paper</span>
                <code style={{ fontSize: 10, background: 'var(--loan-bg-card-alt)', padding: '2px 6px', borderRadius: 4 }}>
                  {demoState.stampDocumentId.slice(0, 16)}…
                </code>
              </div>
            )}
            {demoState.envelopeId && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>eSign Envelope</span>
                <code style={{ fontSize: 10, background: 'var(--loan-bg-card-alt)', padding: '2px 6px', borderRadius: 4 }}>
                  {demoState.envelopeId.slice(0, 16)}…
                </code>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '8px 0 0' }}>
        <p style={{ fontSize: 12, color: 'var(--loan-text-tertiary)', lineHeight: 1.6, marginBottom: 20 }}>
          This demo showcased the entire Matrica API suite —
          document creation, legal stamping, and Aadhaar eSign.
          All API calls hit live backend endpoints.
        </p>

        <button className="loan-btn loan-btn--primary" onClick={onRestart}>
          ↻ Restart Demo
        </button>

        <div className="loan-trust-note" style={{ marginTop: 14 }}>
          Powered by Matrica Solutions Pvt. Ltd.
        </div>
      </div>
    </>
  );
};

export default Step5Success;
